import { Check, Copy } from 'lucide-react';
import { Highlight, themes } from 'prism-react-renderer';
import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { useTheme } from '@/providers';

interface CodeBlockProps {
  children: React.ReactNode;
  className?: string;
  density?: 'default' | 'compact';
  language?: string;
  title?: string;
  /**
   * Skip Prism syntax highlighting and render plain text. Used while a code
   * block is still streaming in: re-tokenizing the whole growing block on
   * every typewriter frame is O(n^2), so we defer highlighting until the
   * block finalizes.
   */
  streaming?: boolean;
}

// Extract text content from React nodes
function getTextContent(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(getTextContent).join('');
  if (typeof node === 'object' && node !== null && 'props' in node) {
    const element = node as React.ReactElement<{ children?: React.ReactNode }>;
    return getTextContent(element.props.children);
  }
  return '';
}

export function CodeBlock({
  children,
  className,
  density = 'default',
  language,
  title,
  streaming = false,
}: CodeBlockProps) {
  const [copied, setCopied] = React.useState(false);
  const { resolvedTheme } = useTheme();
  const codeString = getTextContent(children);
  const isCompact = density === 'compact';
  const showHeader = !isCompact || !!language || !!title;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
    } catch {
      // navigator.clipboard is unavailable on non-secure origins (e.g. plain
      // http://localhost); fall back to the legacy execCommand path so copy
      // still works there instead of failing silently.
      const textarea = document.createElement('textarea');
      textarea.value = codeString;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Map common language aliases
  const normalizeLanguage = (lang?: string): string => {
    if (!lang) return 'text';
    const map: Record<string, string> = {
      js: 'javascript',
      ts: 'typescript',
      py: 'python',
      sh: 'bash',
      shell: 'bash',
      yml: 'yaml',
      md: 'markdown',
      rs: 'rust',
      cpp: 'cpp',
      'c++': 'cpp',
    };
    return map[lang.toLowerCase()] || lang.toLowerCase();
  };

  const normalizedLang = normalizeLanguage(language);
  const isDark = resolvedTheme === 'dark';
  const theme = isDark ? themes.nightOwl : themes.github;
  const copyButton = (
    <button
      type="button"
      onClick={copyToClipboard}
      className={cn(
        'flex cursor-pointer items-center justify-center transition-all duration-150',
        isCompact ? 'size-5 rounded' : 'size-6 rounded-md',
        'text-gray-400 hover:bg-gray-200 hover:text-gray-600',
        'dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-300',
        !showHeader && 'absolute top-1.5 right-1.5 z-10 bg-white/75 backdrop-blur dark:bg-[#011627]/75'
      )}
      aria-label="Copy code"
    >
      {copied ? (
        <Check className={cn(isCompact ? 'size-3' : 'size-3.5', 'text-emerald-500')} />
      ) : (
        <Copy className={isCompact ? 'size-3' : 'size-3.5'} />
      )}
    </button>
  );

  return (
    <div
      className={cn(
        'group/code relative overflow-hidden border',
        isCompact ? 'my-2 rounded-md' : 'my-4 rounded-lg',
        'border-gray-200 dark:border-white/10',
        className
      )}
    >
      {/* Header bar with language badge and copy button */}
      {showHeader ? (
        <div
          className={cn(
            'flex items-center justify-between border-b',
            isCompact ? 'px-3 py-1.5' : 'px-3.5 py-2',
            'border-gray-200 bg-gray-50',
            'dark:border-white/10 dark:bg-[#0d2137]'
          )}
        >
          <div className="flex items-center gap-2">
            {language && (
              <span className="font-medium text-[10px] text-gray-400 uppercase tracking-wider dark:text-gray-500">
                {language}
              </span>
            )}
            {title && (
              <>
                {language && <span className="text-gray-300 dark:text-gray-600">·</span>}
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{title}</span>
              </>
            )}
          </div>
          {copyButton}
        </div>
      ) : (
        copyButton
      )}

      {/* Code block. While streaming, render plain text (no Prism) so the
          growing block isn't re-tokenized every typewriter frame; highlight
          once the block finalizes. */}
      {streaming ? (
        <pre
          className={cn(
            'overflow-x-auto font-mono',
            isCompact ? 'px-3 py-2.5 text-[12px] leading-[1.55]' : 'p-4 text-[13px] leading-[1.7]',
            isDark ? 'text-[#d6deeb]' : 'text-gray-800',
            !showHeader && 'pr-9'
          )}
          style={{ backgroundColor: isDark ? '#011627' : '#f9fafb' }}
        >
          {codeString.trim()}
        </pre>
      ) : (
        <Highlight theme={theme} code={codeString.trim()} language={normalizedLang as any}>
          {({ className: highlightClassName, style, tokens, getLineProps, getTokenProps }) => (
            <pre
              className={cn(
                highlightClassName,
                'overflow-x-auto font-mono',
                isCompact ? 'px-3 py-2.5 text-[12px] leading-[1.55]' : 'p-4 text-[13px] leading-[1.7]',
                !showHeader && 'pr-9'
              )}
              style={{
                ...style,
                backgroundColor: isDark ? '#011627' : '#f9fafb',
              }}
            >
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </pre>
          )}
        </Highlight>
      )}
    </div>
  );
}

// Inline code styling
export function InlineCode({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <code
      className={cn(
        'inline rounded-[4px] px-[0.4em] py-[0.15em] font-mono text-[0.85em]',
        'border border-gray-200 bg-gray-100 text-gray-700',
        'dark:border-white/15 dark:bg-[#0d2137] dark:text-[#d6deeb]',
        className
      )}
    >
      {children}
    </code>
  );
}
